import React, { useEffect, useState } from "react";
import {
    Box, Button, Chip, CircularProgress, Grid, IconButton,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, Typography, Alert
} from "@mui/material";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import {
    getPersonDocuments,
    getDocumentTypes,
    deletePersonDocument,
    getDocumentDownloadUrl
} from "../../store/actions/documentActions";
import DocumentUploadDialog from "./DocumentUploadDialog";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import DocumentChecklist from "./DocumentChecklist";
import DocumentScanDialog from "./DocumentScanDialog";
import QRCodeScanDialog from "./QRCodeScanDialog";
import { iAluno } from "../../types/iAluno";

interface Props {
    registro?: iAluno;
}

const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDate = (date?: string | Date): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
};

const getFileIcon = (mimeType?: string) => {
    if (mimeType?.startsWith('image/')) return <ImageIcon color="primary" />;
    return <DescriptionIcon color="error" />;
};

const AlunoTabDocumentos = (props: Props) => {
    const dispatch = useDispatch();
    const personId = props.registro?.id;

    const documents = useSelector((state: RootState) => state.cadastros.personDocuments);
    const documentTypes = useSelector((state: RootState) => state.cadastros.documentTypes);
    const isFetching = useSelector((state: RootState) => state.cadastros.isFetchingDocuments);
    const isUploading = useSelector((state: RootState) => state.cadastros.isUploadingDocument);
    const isDeleting = useSelector((state: RootState) => state.cadastros.isDeletingDocument);

    const [uploadOpen, setUploadOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState<iPersonDocument | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [scanOpen, setScanOpen] = useState(false);
    const [scannedFile, setScannedFile] = useState<File | undefined>(undefined);
    const [hasCamera, setHasCamera] = useState(false);
    const [qrCodeOpen, setQrCodeOpen] = useState(false);

    // Check if camera is available
    useEffect(() => {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            navigator.mediaDevices.enumerateDevices().then((devices) => {
                setHasCamera(devices.some((d) => d.kind === 'videoinput'));
            }).catch(() => setHasCamera(false));
        }
    }, []);

    useEffect(() => {
        if (personId && personId > 0) {
            dispatch(getPersonDocuments(personId) as any);
            dispatch(getDocumentTypes() as any);
        }
    }, [dispatch, personId]);

    const handlePreview = (doc: iPersonDocument) => {
        setPreviewDoc(doc);
        setPreviewOpen(true);
    };

    const handleDownload = (doc: iPersonDocument) => {
        if (doc.fileName) {
            const url = getDocumentDownloadUrl(doc.fileName);
            const token = localStorage.getItem('token-id');
            // Open download with auth
            const link = document.createElement('a');
            link.href = url + (token ? `?access_token=${token}` : '');
            link.target = '_blank';
            link.download = doc.originalName || doc.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleDelete = (docId: number) => {
        if (deleteConfirm === docId) {
            if (personId) {
                dispatch(deletePersonDocument(docId, personId) as any);
            }
            setDeleteConfirm(null);
        } else {
            setDeleteConfirm(docId);
            setTimeout(() => setDeleteConfirm(null), 3000);
        }
    };

    const handleScanCapture = (file: File) => {
        setScannedFile(file);
        setScanOpen(false);
        setUploadOpen(true);
    };

    const handleUploadClose = () => {
        setUploadOpen(false);
        setScannedFile(undefined);
    };

    // Calculate required documents missing
    const requiredTypes = documentTypes.filter((dt: iDocumentType) => dt.required);
    const uploadedTypeIds = documents.map((d: iPersonDocument) => d.documentTypeId);
    const missingRequired = requiredTypes.filter((dt: iDocumentType) => !uploadedTypeIds.includes(dt.id));

    if (isFetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header with upload button and missing docs indicator */}
            <Grid container sx={{ mb: 2, alignItems: 'center' }}>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Documentos
                        </Typography>
                        {missingRequired.length > 0 && (
                            <Chip
                                label={`${missingRequired.length} obrigatório(s) faltando`}
                                color="warning"
                                size="small"
                            />
                        )}
                        {missingRequired.length === 0 && requiredTypes.length > 0 && (
                            <Chip label="Completo" color="success" size="small" />
                        )}
                    </Box>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<PhoneAndroidIcon />}
                        onClick={() => setQrCodeOpen(true)}
                        size="small"
                    >
                        Digitalizar por Celular
                    </Button>
                    {hasCamera && (
                        <Button
                            variant="outlined"
                            startIcon={<DocumentScannerIcon />}
                            onClick={() => setScanOpen(true)}
                            size="small"
                        >
                            Digitalizar
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setUploadOpen(true)}
                        disabled={isUploading}
                        size="small"
                    >
                        {isUploading ? 'Enviando...' : 'Adicionar Documento'}
                    </Button>
                </Grid>
            </Grid>

            {/* Missing required documents alert */}
            {missingRequired.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Documentos obrigatórios pendentes: {missingRequired.map((dt: iDocumentType) => dt.name).join(', ')}
                </Alert>
            )}

            {/* Checklist de Documentos Obrigatórios */}
            <DocumentChecklist documentTypes={documentTypes} documents={documents} />

            {/* Documents table */}
            {documents.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        Nenhum documento cadastrado.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width={40}></TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Nome do Arquivo</TableCell>
                                <TableCell>Data Upload</TableCell>
                                <TableCell>Tamanho</TableCell>
                                <TableCell align="center" width={140}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documents.map((doc: iPersonDocument) => (
                                <TableRow key={doc.id} hover>
                                    <TableCell>{getFileIcon(doc.mimeType)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {doc.documentType?.name || '-'}
                                            {doc.documentType?.required && (
                                                <Chip label="Obrig." size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{doc.originalName || doc.fileName}</TableCell>
                                    <TableCell>{formatDate(doc.uploadDate)}</TableCell>
                                    <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Visualizar">
                                            <IconButton size="small" onClick={() => handlePreview(doc)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download">
                                            <IconButton size="small" onClick={() => handleDownload(doc)}>
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={deleteConfirm === doc.id ? "Clique novamente para confirmar" : "Excluir"}>
                                            <IconButton
                                                size="small"
                                                color={deleteConfirm === doc.id ? "error" : "default"}
                                                onClick={() => doc.id && handleDelete(doc.id)}
                                                disabled={isDeleting}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Upload Dialog */}
            <DocumentUploadDialog
                open={uploadOpen}
                onClose={handleUploadClose}
                personId={personId || 0}
                documentTypes={documentTypes}
                initialFile={scannedFile}
            />

            {/* Scan Dialog */}
            <DocumentScanDialog
                open={scanOpen}
                onClose={() => setScanOpen(false)}
                onCapture={handleScanCapture}
            />

            {/* Preview Dialog */}
            <DocumentPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                document={previewDoc}
            />

            {/* QR Code Mobile Scan Dialog */}
            <QRCodeScanDialog
                open={qrCodeOpen}
                onClose={() => setQrCodeOpen(false)}
                personId={personId || 0}
                personName={props.registro?.Name}
            />
        </Box>
    );
};

export default AlunoTabDocumentos;
